import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePatientAuth } from "@/contexts/PatientAuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import useQueueSocket from "@/hooks/useQueueSocket";
import LanguageToggle from "@/components/LanguageToggle";
import FamilyManager from "@/components/FamilyManager";
import { fileUrl, API } from "@/api";
import axios from "axios";
import { HeartPulse, LogOut, Search, Stethoscope, Clock, Hash, MapPin, ArrowRight, CalendarCheck2, AlertCircle, TimerReset, AlertOctagon, CalendarPlus, RefreshCcw, CalendarDays } from "lucide-react";
import { toast } from "sonner";

export default function PatientHome() {
  const { t } = useI18n();
  const { user, logout, api } = usePatientAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [{ data: b }, { data: f }] = await Promise.all([
        api.get("/patient/me/bookings"),
        api.get("/patient/me/followups").catch(() => ({ data: [] })),
      ]);
      setBookings(b);
      setFollowups(f || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const isProviderCancelled = (b) =>
    b.appointment.status === "cancelled" &&
    b.appointment.cancelled_reason === "provider_leave";

  const providerCancelled = bookings.filter(isProviderCancelled);
  const active = bookings.filter(
    (b) => !["completed", "cancelled"].includes(b.appointment.status)
  );
  const past = bookings.filter((b) => b.appointment.status === "completed");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-home-page">
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between bg-white border-b sticky top-0 z-10">
        <Link to="/patient" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
            <HeartPulse className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-sm font-bold text-slate-900">UpchaarQ Care</div>
            <div className="text-xs text-slate-500">{user?.mobile}</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/patient"); }} data-testid="patient-logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="px-4 sm:px-8 py-6 sm:py-10 max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">My Bookings</h1>
          <Link to="/patient/search">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white gap-2" data-testid="patient-find-doctor-cta">
              <Search className="w-4 h-4" /> {t("find_doctor")}
            </Button>
          </Link>
          <FamilyManager />
        </div>

        {loading ? (
          <p className="text-slate-500">{t("loading")}</p>
        ) : active.length === 0 && past.length === 0 ? (
          <Card className="medical-card p-10 text-center" data-testid="patient-no-bookings">
            <CalendarCheck2 className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-3">No bookings yet — find a doctor to get started.</p>
          </Card>
        ) : (
          <>
            {/* Provider-cancelled (Doctor on urgent leave) */}
            {providerCancelled.map((b) => <ProviderCancelledCard key={b.appointment.id} booking={b} />)}

            {/* Upcoming follow-ups */}
            {followups.length > 0 && <UpcomingFollowupsCard rows={followups} />}

            {/* Active */}
            {active.map((b) => <ActiveBookingCard key={b.appointment.id} booking={b} reload={load} />)}

            {/* Past */}
            {past.length > 0 && (
              <div className="mt-8">
                <h2 className="font-heading text-base font-semibold text-slate-500 mb-3">Past visits</h2>
                <div className="space-y-2">
                  {past.map((b) => (
                    <Card key={b.appointment.id} className="medical-card p-4 flex items-center gap-3" data-testid={`past-${b.appointment.id}`}>
                      <Badge variant="outline" className="font-mono">#{b.appointment.ticket_number}</Badge>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{b.doctor.name}</div>
                        <div className="text-xs text-slate-500">{b.appointment.date}</div>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">completed</Badge>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function ProviderCancelledCard({ booking }) {
  const { appointment, doctor, org } = booking;
  const navigate = useNavigate();
  const [nextDate, setNextDate] = useState(appointment.suggested_next_date || null);
  const [loading, setLoading] = useState(false);
  const wasPaid = ["paid", "paid_self_reported"].includes(appointment.payment_status);

  useEffect(() => {
    if (nextDate || !doctor?.id) return;
    setLoading(true);
    axios.get(`${API}/public/doctors/${doctor.id}/next-available`)
      .then(({ data }) => setNextDate(data?.next_available_date || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [doctor?.id, nextDate]);

  const goRebook = () => {
    navigate(`/patient/doctor/${doctor.id}`);
  };

  return (
    <Card className="medical-card p-5 sm:p-6 border-rose-200 bg-gradient-to-br from-rose-50 to-white" data-testid={`provider-cancelled-${appointment.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
          <AlertOctagon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-rose-600 text-white hover:bg-rose-600 uppercase text-[10px] tracking-wider">Doctor on urgent leave</Badge>
            <Badge variant="outline" className="font-mono text-xs">#{appointment.ticket_number || "—"}</Badge>
          </div>
          <h3 className="font-heading text-lg font-bold text-slate-900 mt-2">{doctor.name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{org?.name}{org?.city ? `, ${org.city}` : ""}</p>
          <p className="text-sm text-slate-700 mt-3">
            We regret to inform you that <span className="font-semibold">Dr. {doctor.name}</span> is on urgent leave today. Your booking has been cancelled
            {wasPaid
              ? <> and a <span className="font-semibold text-emerald-700">100% full refund of ₹{appointment.fee}</span> has been initiated to your source account (24-48h).</>
              : <>. No payment was charged.</>}
          </p>

          {nextDate && (
            <div className="mt-4 rounded-lg bg-white border border-slate-200 p-3 flex items-center justify-between gap-2" data-testid={`next-available-${appointment.id}`}>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">Next available</div>
                <div className="font-mono font-bold text-slate-900 mt-0.5">{nextDate}</div>
              </div>
              <CalendarPlus className="w-5 h-5 text-emerald-600" />
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={goRebook} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 h-10" data-testid={`rebook-cta-${appointment.id}`}>
              <RefreshCcw className="w-4 h-4" />
              {nextDate ? `Rebook for ${nextDate}` : "Rebook this doctor"}
            </Button>
            <Link to="/patient/search">
              <Button variant="outline" className="gap-2 h-10" data-testid={`other-doctors-${appointment.id}`}>
                <Search className="w-4 h-4" /> Find another doctor
              </Button>
            </Link>
          </div>
          {loading && <p className="text-xs text-slate-400 mt-2">Finding next available slot…</p>}
        </div>
      </div>
    </Card>
  );
}


function ActiveBookingCard({ booking, reload }) {
  const { appointment, doctor, org, estimated_wait_minutes } = booking;
  const navigate = useNavigate();
  const { api } = usePatientAuth();
  const liveSnap = useQueueSocket(doctor.id);
  const [refunding, setRefunding] = useState(false);

  // Live counters: your token vs current checking
  const yourToken = appointment.ticket_number;
  const currentToken = liveSnap?.current_token ?? booking.current_token;

  // Recompute position when queue moves
  const completedCount = liveSnap?.completed_count;
  // If our ticket > current_token we can compute position locally
  let position = booking.position;
  if (currentToken && appointment.status === "waiting") {
    const yt = parseInt(yourToken);
    const ct = parseInt(currentToken);
    if (!isNaN(yt) && !isNaN(ct)) position = Math.max(1, yt - ct);
  }
  const eta = position * (doctor.avg_diagnosis_time || 10);
  const delayMin = liveSnap?.delay_minutes ?? 0;
  const eligibleForAutoRefund = delayMin >= 30 && (appointment.status === "waiting" || appointment.status === "pending_payment");

  const requestAutoRefund = async () => {
    if (refunding) return;
    const ok = window.confirm(`Doctor announced a ${delayMin} min delay. Cancel this booking and request a refund?`);
    if (!ok) return;
    setRefunding(true);
    try {
      const { data } = await api.post(`/patient/me/bookings/${appointment.id}/cancel-refund`);
      if (data.refund_status === "initiated") {
        toast.success(`Refund of ₹${data.refund_amount} initiated. UPI credit in 24-48h.`);
      } else {
        toast.success("Booking cancelled.");
      }
      reload && reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setRefunding(false);
    }
  };

  return (
    <Card className="medical-card p-5 sm:p-6 relative overflow-hidden" data-testid={`active-${appointment.id}`}>
      <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-sky-500 to-emerald-500" />
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center shrink-0">
          {doctor.profile_pic ? <img src={fileUrl(doctor.profile_pic)} alt="" className="w-full h-full object-cover" /> : <Stethoscope className="w-7 h-7 text-sky-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-semibold text-slate-900 truncate">{doctor.name}</h3>
          <p className="text-xs text-slate-500">{doctor.specialization_en}</p>
          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {org?.name}{org?.city ? `, ${org.city}` : ""}{doctor.room_number ? ` · Room ${doctor.room_number}` : ""}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5">
        <LiveTile label="Your Token" value={`#${yourToken}`} accent="sky" testid="live-your-token" />
        <LiveTile label="Current Checking" value={currentToken ? `#${currentToken}` : "—"} accent="emerald" testid="live-current-token" />
      </div>

      {appointment.status === "waiting" && currentToken && (
        <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm flex items-center justify-between">
          <div>
            <Hash className="inline w-3.5 h-3.5 mr-1 text-slate-400" />
            Position: <span className="font-semibold text-slate-900">#{position}</span>
          </div>
          <div>
            <Clock className="inline w-3.5 h-3.5 mr-1 text-slate-400" />
            ETA: <span className="font-semibold text-sky-600">{eta} min</span>
          </div>
        </div>
      )}

      {appointment.status === "checking" && (
        <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700 font-semibold flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          It&apos;s your turn — please go to the cabin.
        </div>
      )}

      {/* Slot recycling: warn at 3 slots behind */}
      {appointment.status === "waiting" && position > 0 && position <= 3 && (
        <div className="mt-3 rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-800 flex items-start gap-2" data-testid={`recycle-warn-${appointment.id}`}>
          <TimerReset className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            {position === 1
              ? <>You&apos;re next — please be ready outside the cabin. If you don&apos;t respond within 60 seconds of being called, you&apos;ll be moved back ~3 positions.</>
              : <>Only {position - 1} patient(s) ahead. Stay close by — be ready within 60s when called.</>}
          </div>
        </div>
      )}

      {/* Auto-refund link when doctor announced >=30 min delay */}
      {eligibleForAutoRefund && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2" data-testid={`refund-banner-${appointment.id}`}>
          <AlertCircle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
          <div className="flex-1 text-xs text-amber-900">
            <p className="font-semibold">Doctor announced ~{delayMin} min delay.</p>
            <p>You can cancel now and get a UPI auto-refund within 24-48h.</p>
            <Button
              variant="outline"
              size="sm"
              disabled={refunding}
              onClick={requestAutoRefund}
              className="mt-2 h-8 border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
              data-testid={`request-refund-${appointment.id}`}
            >
              {refunding ? "Cancelling…" : "Cancel & Request Auto-Refund"}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <Badge variant={appointment.payment_status === "paid_self_reported" ? "default" : "outline"} className={appointment.payment_status === "paid_self_reported" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}>
          {appointment.payment_status === "paid_self_reported" ? "Paid" : "Payment pending"}
        </Badge>
        <Button onClick={() => navigate(`/patient/slip/${appointment.id}`)} variant="ghost" size="sm" className="gap-1 text-sky-600" data-testid={`open-slip-${appointment.id}`}>
          Open slip <ArrowRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </Card>
  );
}

function UpcomingFollowupsCard({ rows }) {
  const navigate = useNavigate();
  return (
    <Card className="medical-card p-5 sm:p-6 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="patient-upcoming-followups">
      <div className="flex items-center gap-2 mb-3">
        <CalendarDays className="w-4 h-4 text-emerald-600" />
        <h3 className="font-heading text-lg font-semibold text-slate-900">Upcoming follow-ups</h3>
        <Badge className="bg-emerald-500 text-white hover:bg-emerald-500 ml-auto">{rows.length}</Badge>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`followup-row-${r.id}`}>
            <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <CalendarPlus className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Follow-up on <span className="font-mono font-semibold text-slate-800">{r.followup_date}</span></div>
              <div className="font-semibold text-slate-900 truncate">Dr. {r.doctor?.name || r.doctor_name}</div>
              {r.followup_note && <div className="text-xs text-slate-500 truncate">{r.followup_note}</div>}
              {r.org?.name && <div className="text-[11px] text-slate-400 truncate">{r.org.name}{r.org.city ? `, ${r.org.city}` : ""}</div>}
            </div>
            {r.doctor?.id && (
              <Button
                size="sm"
                onClick={() => navigate(`/patient/doctor/${r.doctor.id}`)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1 h-8"
                data-testid={`followup-book-${r.id}`}
              >
                Book <ArrowRight className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}


function LiveTile({ label, value, accent, testid }) {
  const colors = {
    sky: "from-sky-50 to-sky-100 text-sky-700 border-sky-200",
    emerald: "from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200",
  };
  return (
    <div className={`rounded-xl border p-4 text-center bg-gradient-to-br ${colors[accent]}`} data-testid={testid}>
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold opacity-80">{label}</div>
      <div className="font-heading text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}
